import type { VFrameBase } from '@festina/life-general-types/life'
import {
  type AllKeys,
  asyncUtils,
  isEmpty,
  type Optional,
} from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useDataContext } from '@festina-life/ui/theme'
import get from 'lodash-es/get'
import {
  type PropsWithChildren,
  useEffect,
  useEffectEvent,
  useRef,
} from 'react'

import { modifyNameToFirstEntry } from './formUtility'
import { Provider } from './frameContext'
import { pickFields } from './FrameProvider.utils'
import { useLifeFormikContext } from './useLifeFormikContext'
import { isBooleanVFrame, isEnumeratedVFrame } from './yup/validation/types'

export const FETCH_FRAME_DEBOUNCE_MS = 1500

type Props<
  T extends { _type: string },
  F extends VFrameBase,
> = PropsWithChildren<{
  fetchFrame: (values: Partial<T> & { _type: string }) => Promise<F | null>
  customVFrameGetter?: ({
    name,
    frame,
    values,
  }: {
    name: string
    frame: Optional<F>
    values: T
  }) => Optional<VFrameBase>
  /**
   * Should only refer to leaf fields.
   *
   * For example, it should not refer to array positions such as `"foo.1"`.
   */
  relevantFrameFields?: Extract<AllKeys<T>, string>[]
  frame: F | null
  onFrameChanged: (frame: Optional<F>) => void
  onFrameIsLoadingChanged: (isLoading: boolean) => void
}>

export const FrameProvider = <
  T extends { _type: string },
  F extends VFrameBase,
>({
  fetchFrame,
  customVFrameGetter,
  relevantFrameFields = [],
  frame,
  onFrameChanged,
  onFrameIsLoadingChanged,
  children,
}: Props<T, F>) => {
  const translate = useTranslate()

  // We could technically avoid creating a separate `FrameProvider`, and instead
  // store the `frame` and `fieldVFrame` getter inside Formik's `status`.
  //
  // However, because we also need to read from Formik's context to figure out
  // what the current values are to fetch the frame, it feels cleaner to just
  // have a separate context underneath and avoid writing up unnecessarily.
  const { values } = useLifeFormikContext<T>()
  const { shouldSkipFrame } = useDataContext()

  const fieldVFrame = (name: string): Optional<VFrameBase> =>
    customVFrameGetter
      ? customVFrameGetter({ name, frame: frame as F, values })
      : get(frame, modifyNameToFirstEntry(name))

  // Finding which relevant fields have changed is used
  // to trigger a new frame fetch if there are any.
  const prevValueRef = useRef<T>(values)
  useEffect(() => {
    prevValueRef.current = values
  }, [values])
  const changedFields = relevantFrameFields.filter(
    // No need for `isEqual` because relevant fields are never objects in practice.
    (field) => get(values, field) !== get(prevValueRef.current, field),
  )

  const relevantFrameValuesDependency = JSON.stringify(
    // Can't use the returned object directly as that's not a stable dependency.
    pickFields(values, relevantFrameFields),
  )

  const fetchNumber = useRef(0)
  const fetchNewFrame = useEffectEvent(() => {
    if (shouldSkipFrame || !changedFields.length) {
      return
    }

    // This effect event may be called more than once.
    // The number is used to avoid setting state with a stale fetch response.
    // Effect destructors cannot be used for this because the fetch doesn't always need to occur.
    const currentNumber = ++fetchNumber.current

    const areAllChangedFieldsEnumerated = changedFields.every((field) => {
      const vFrame = fieldVFrame(field)
      // In a `LifeInput` context, both the enumerated and boolean VFrames render as a select.
      return isEnumeratedVFrame(vFrame) || isBooleanVFrame(vFrame)
    })

    void (async () => {
      try {
        if (!areAllChangedFieldsEnumerated) {
          // Don't hit the network immediately if the user is typing and not just using a select.
          await asyncUtils.sleep(FETCH_FRAME_DEBOUNCE_MS)

          // If the user has triggered another fetch while we were waiting then skip this fetch.
          if (currentNumber !== fetchNumber.current) {
            return
          }
        }

        // This triggers loading state to show the user that the frame is being fetched so we deliberately only call it once we are about to fetch the frame.
        onFrameIsLoadingChanged(true)

        const result = await fetchFrame(pickFields(values, relevantFrameFields))
        if (currentNumber === fetchNumber.current) {
          onFrameChanged(result)
        }
      } catch {
        if (currentNumber === fetchNumber.current) {
          onFrameChanged(null)
        }
      } finally {
        if (currentNumber === fetchNumber.current) {
          onFrameIsLoadingChanged(false)
        }
      }
    })()
  })
  useEffect(() => {
    fetchNewFrame()
  }, [relevantFrameValuesDependency])

  const isMissingFrame = !shouldSkipFrame && isEmpty(frame)

  return (
    <Provider
      value={{
        frame,
        fieldVFrame,
      }}
    >
      {isMissingFrame && (
        <Alert severity="error">{translate('form.missingFrame.warning')}</Alert>
      )}
      {children}
    </Provider>
  )
}
